/**
 * 批次匯入表單(UI-02)
 * 自 ManualTrading 抽出 — 舊版定義在元件內部,每次父層 render 都產生新的
 * 函式元件型別,整個批次匯入子樹(含上千列預覽 Table)會 unmount/remount。
 */

import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { StockNameManager } from '../../services/stockNameManager';
import {
  CheckCircle,
  CloudUpload,
  Delete,
  FileDownload,
  Error as ErrorIcon,
} from '@mui/icons-material';

/** 批次交易資料型別(與 ManualTrading 共用) */
export interface BatchTransaction {
  symbol: string;
  stockName?: string;
  type: 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease';
  quantity: number;
  price: number;
  date: string;
  fees?: number;
  taxes?: number;
  adjustmentAmount?: number;
  adjustmentReason?: string;
  notes?: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  isValid: boolean;
  errors: string[];
}

export interface ImportProgressInfo {
  current: number;
  total: number;
  currentItem: string;
  phase: 'validation' | 'import' | 'calculation' | 'complete';
}

export interface ImportResultInfo {
  success: number;
  failed: number;
  errors: string[];
}

export const csvTemplate = `日期,股票代號,股票名稱,交易類型,股數,價格,手續費,交易稅,調整總額,調整原因,備註
2024-01-15,2330,台積電,buy,1000,580.00,20,0,100,手續費調整,台積電買入
2024-01-16,2317,鴻海,sell,500,85.50,15,128,-50,交易稅減免,鴻海賣出
2024-01-17,2330,台積電,dividend,1000,2.5,10,0,25,股利稅加算,台積電股利
2024-01-18,CASH,現金,cash_deposit,1,50000,0,0,,,存入資金
2024-01-19,CASH,現金,cash_withdrawal,1,10000,0,0,,,提取資金
2024-01-20,2330,台積電,stock_increase,100,0,0,0,,,股票股利增加
2024-01-21,2317,鴻海,stock_decrease,50,0,0,0,,,股票分割調整

英文標題格式範例：
date,symbol,stockName,type,quantity,price,fees,taxes,adjustmentAmount,adjustmentReason,notes
2024-01-15,2330,Taiwan Semiconductor,buy,1000,580.00,20,0,100,Fee adjustment,Taiwan Semiconductor buy
2024-01-16,2317,Hon Hai,sell,500,85.50,15,128,-50,Tax reduction,Hon Hai sell
2024-01-17,2330,Taiwan Semiconductor,dividend,1000,2.5,10,0,25,Tax calculation,Taiwan Semiconductor dividend
2024-01-18,CASH,Cash,cash_deposit,1,50000,0,0,,,Cash deposit
2024-01-19,CASH,Cash,cash_withdrawal,1,10000,0,0,,,Cash withdrawal
2024-01-20,2330,Taiwan Semiconductor,stock_increase,100,0,0,0,,,Stock dividend increase
2024-01-21,2317,Hon Hai,stock_decrease,50,0,0,0,,,Stock split adjustment`;

interface BatchImportFormProps {
batchTransactions: BatchTransaction[];
batchProcessing: boolean;
importProgress: ImportProgressInfo | null;
importResult: ImportResultInfo | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
onBatchImport: () => void;
onDownloadTemplate: () => void;
onClearBatchData: () => void;
}

const BatchImportForm: React.FC<BatchImportFormProps> = ({
batchTransactions,
batchProcessing,
importProgress,
importResult,
fileInputRef,
onFileUpload,
onBatchImport,
onDownloadTemplate,
onClearBatchData,
}) => (

// 批次匯入表單組件
    <>
      {/* 說明和範例下載 */}
      <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📋 匯入說明
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            支持 CSV 格式文件批次匯入交易記錄，請按照以下格式準備您的數據：
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom color="primary">
            📋 欄位說明（必填★ / 可選☆）：
          </Typography>
          <Box sx={{ mb: 3, pl: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ★ <strong>日期</strong> (date)：格式 YYYY-MM-DD，如 2024-01-15
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ★ <strong>股票代號</strong> (symbol)：股票代碼或 "CASH"（資金調整時）
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ☆ <strong>股票名稱</strong> (stockName)：可選，自定義股票名稱，如未填寫則使用系統預設名稱
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ★ <strong>交易類型</strong> (type)：buy/sell/dividend/cash_deposit/cash_withdrawal/stock_increase/stock_decrease
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ★ <strong>股數</strong> (quantity)：正整數，資金調整時固定填 1
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ★ <strong>價格</strong> (price)：成交價或調整金額，股票異動時填 0
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ☆ <strong>手續費</strong> (fees)：可選，未填則系統自動計算
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ☆ <strong>交易稅</strong> (taxes)：可選，未填則系統自動計算
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ☆ <strong>調整總額</strong> (adjustmentAmount)：可選，對單筆交易的金額調整，正值增加負值減少
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ☆ <strong>調整原因</strong> (adjustmentReason)：可選，調整金額的原因說明
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              ☆ <strong>備註</strong> (notes)：可選，交易說明
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={onDownloadTemplate}
            >
              下載範例檔案
            </Button>
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            交易類型說明：
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="buy" size="small" color="success" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                買入股票 - 記錄股票購買交易，會增加持股數量
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="sell" size="small" color="error" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                賣出股票 - 記錄股票賣出交易，會減少持股數量
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="dividend" size="small" color="warning" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                股利收入 - 記錄現金股利或股票股利，股數填寫持有股數，價格填寫每股股利
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="cash_deposit" size="small" color="success" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                資金存入 - 記錄資金存入，股票代號填寫 "CASH"，股數固定為 1，價格為存入金額
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="cash_withdrawal" size="small" color="error" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                資金提取 - 記錄資金提取，股票代號填寫 "CASH"，股數固定為 1，價格為提取金額
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="stock_increase" size="small" color="info" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                股票增加 - 記錄股票數量增加（如股票股利、無償配股等），價格固定為 0
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip label="stock_decrease" size="small" color="warning" sx={{ mr: 1, minWidth: 80 }} />
              <Typography variant="body2" color="text.secondary">
                股票減少 - 記錄股票數量減少（如股票分割調整、減資等），價格固定為 0
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            CSV 格式範例：
          </Typography>
          <Box sx={{ 
            backgroundColor: 'grey.100', 
            p: 2, 
            borderRadius: 1, 
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            overflow: 'auto'
          }}>
            <Typography variant="body2" component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{csvTemplate}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 檔案上傳 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          上傳 CSV 檔案
        </Typography>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={onFileUpload}
          style={{ display: 'none' }}
        />
        
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ mr: 2 }}
        >
          選擇檔案
        </Button>
        
        {batchTransactions.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={onClearBatchData}
            color="error"
          >
            清除資料
          </Button>
        )}
      </Paper>

      {/* 資料預覽和驗證結果 */}
      {batchTransactions.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              資料預覽 ({batchTransactions.length} 筆)
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              onClick={onBatchImport}
              disabled={batchProcessing || batchTransactions.filter(t => t.isValid).length === 0}
              startIcon={batchProcessing ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {batchProcessing ? '匯入中...' : `匯入 ${batchTransactions.filter(t => t.isValid).length} 筆有效資料`}
            </Button>
          </Box>

          {/* 詳細的進度追蹤 */}
          {batchProcessing && (
            <Box sx={{ mb: 3 }}>
              {importProgress && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {importProgress.phase === 'validation' && '🔍 驗證資料...'}
                      {importProgress.phase === 'import' && '📦 匯入交易...'}
                      {importProgress.phase === 'calculation' && '🔄 更新帳戶...'}
                      {importProgress.phase === 'complete' && '✅ 匯入完成'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {importProgress.current}/{importProgress.total}
                    </Typography>
                  </Box>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={(importProgress.current / importProgress.total) * 100}
                    sx={{ 
                      mb: 1,
                      height: 8,
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: importProgress.phase === 'complete' ? '#4caf50' : '#1976d2'
                      }
                    }} 
                  />
                  
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {importProgress.currentItem}
                  </Typography>
                </>
              )}
            </Box>
          )}

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>狀態</TableCell>
                  <TableCell>日期</TableCell>
                  <TableCell>股票代號</TableCell>
                  <TableCell>股票名稱</TableCell>
                  <TableCell>類型</TableCell>
                  <TableCell>股數</TableCell>
                  <TableCell>價格</TableCell>
                  <TableCell>手續費</TableCell>
                  <TableCell>交易稅</TableCell>
                  <TableCell>其他</TableCell>
                  <TableCell>備註</TableCell>
                  <TableCell>錯誤訊息</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchTransactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {transaction.status === 'success' ? (
                        <CheckCircle color="success" />
                      ) : transaction.status === 'error' ? (
                        <ErrorIcon color="error" />
                      ) : transaction.isValid ? (
                        <CheckCircle color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </TableCell>
                    <TableCell>{transaction.date}</TableCell>
                    <TableCell>{transaction.symbol}</TableCell>
                    <TableCell>
                      {transaction.stockName || StockNameManager.getStockName(transaction.symbol)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(() => {
                          const typeMap: Record<string, string> = {
                            'buy': '買入',
                            'sell': '賣出',
                            'dividend': '股利',
                            'cash_deposit': '存入資金',
                            'cash_withdrawal': '提取資金',
                            'stock_increase': '股票增加',
                            'stock_decrease': '股票減少'
                          };
                          return typeMap[transaction.type] || transaction.type;
                        })()}
                        size="small"
                        color={
                          transaction.type === 'buy' ? 'success' :
                          transaction.type === 'sell' ? 'error' :
                          transaction.type === 'dividend' ? 'warning' : 'secondary'
                        }
                      />
                    </TableCell>
                    <TableCell>{transaction.quantity}</TableCell>
                    <TableCell>${transaction.price}</TableCell>
                    <TableCell>${transaction.fees || 0}</TableCell>
                    <TableCell>${transaction.taxes || 0}</TableCell>
                    <TableCell>
                      {transaction.adjustmentAmount !== undefined && transaction.adjustmentAmount !== 0 ? (
                        <Tooltip 
                          title={
                            <Box>
                              {transaction.adjustmentReason ? (
                                <Typography variant="caption">{transaction.adjustmentReason}</Typography>
                              ) : (
                                <Typography variant="caption">調整金額: {transaction.adjustmentAmount >= 0 ? '+' : ''}${transaction.adjustmentAmount}</Typography>
                              )}
                            </Box>
                          }
                        >
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ cursor: 'help', fontSize: '0.75rem' }}
                          >
                            ✓
                          </Typography>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{transaction.notes}</TableCell>
                    <TableCell>
                      {transaction.errors.map((error, i) => (
                        <Typography key={i} variant="caption" color="error" display="block">
                          {error}
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* 匯入結果 */}
      {importResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            匯入結果
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip 
              icon={<CheckCircle />} 
              label={`成功 ${importResult.success} 筆`} 
              color="success" 
            />
            {importResult.failed > 0 && (
              <Chip 
                icon={<ErrorIcon />} 
                label={`失敗 ${importResult.failed} 筆`} 
                color="error" 
              />
            )}
          </Box>

          {importResult.errors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="error" gutterBottom>
                錯誤詳情：
              </Typography>
              {importResult.errors.map((error, index) => (
                <Typography key={index} variant="caption" color="error" display="block">
                  • {error}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </>
);

export default BatchImportForm;
